<template>
  <div class="datalayer" />
</template>

<script>

/**
 * Vue: Datalayer
 * -----------------------------------------------------------------------------
 * Used to set datalayer events.
 *
 */

import { mapState } from 'vuex'

export default {
  name: 'Datalayer',

  data() {
    return {}
  },

  computed: {
    ...mapState({
      cartHistory: (state) => state.cart.history,
    }),

    latestCartHistory() {
      return this.cartHistory[0]
    },
  },

  watch: {
    latestCartHistory() {
      if (this.latestCartHistory.action === 'addToCart') {
        cnvs.EventBus.emit('Datalayer:productAddToCart', this.latestCartHistory.items[0])

      } else if (this.latestCartHistory.action === 'removeFromCart') {
        cnvs.EventBus.emit('Datalayer:productRemoveFromCart', this.latestCartHistory.items[0])
        cnvs.EventBus.emit('Datalayer:lineItemRemove', this.latestCartHistory.items[0])
      }
    },
  },

  mounted() {
    this.setNodeSelectors()
    this.setEventListeners()
    this.setEventBusListeners()
  },

  methods: {


    /**
     * Set Node selectors.
     */
    setNodeSelectors() {
    },

    /**
     * Set Event listeners.
     */
    setEventListeners() {
    },

    /**
     * Set EventBus listeners.
     */
    setEventBusListeners() {

      // Datalayer reference: Enhanced e-commerce: Product impression
      cnvs.EventBus.listen('Datalayer:productImpressions', (data) => this.productImpressionsEvent(data))

      // Datalayer reference: Enhanced e-commerce: Product click
      cnvs.EventBus.listen('Datalayer:productClick', (data) => this.productClickEvent(data))

      // Datalayer reference: Enhanced e-commerce: Product detail
      cnvs.EventBus.listen('Datalayer:productDetails', (product) => this.productDetailsEvent(product))

      // Datalayer reference: Enhanced e-commerce: Product add to cart
      cnvs.EventBus.listen('Datalayer:productAddToCart', (data) => this.productAddToCartEvent(data))

      // Datalayer reference: Enhanced e-commerce: Product remove from cart
      cnvs.EventBus.listen('Datalayer:productRemoveFromCart', (data) => this.productRemoveFromCartEvent(data))

      // Datalayer reference: Custom events: 1
      cnvs.EventBus.listen('Datalayer:megaNavLink', (data) => this.megaNavLinkEvent(data))
      cnvs.EventBus.listen('Datalayer:siteHeaderLink', (label) => this.siteHeaderLinkEvent(label))
      cnvs.EventBus.listen('Datalayer:footerLink', (label) => this.footerLinkEvent(label))
      cnvs.EventBus.listen('Datalayer:siteHeaderLogo', () => this.siteHeaderLogoEvent())
      cnvs.EventBus.listen('Datalayer:categoryListing', (data) => this.categoryListingEvent(data))

      // Datalayer reference: Custom events: 2
      cnvs.EventBus.listen('Datalayer:productOutOfStock', (product) => this.productOutOfStockEvent(product))
      cnvs.EventBus.listen('Datalayer:fullscreenProductGallery', (product) => this.fullscreenProductGalleryEvent(product))
      cnvs.EventBus.listen('Datalayer:productAddToWishlist', (productId) => this.productAddToWishlist(productId))
      cnvs.EventBus.listen('Datalayer:productInfoAccordion', (label) => this.productInfoAccordionEvent(label))

      // Datalayer reference: Custom events: 3
      cnvs.EventBus.listen('Datalayer:lineItemRemove', (data) => this.lineItemRemoveEvent(data))

      // Datalayer reference: Custom events: 6
      cnvs.EventBus.listen('Datalayer:searchResultsSorting', (label) => this.searchResultsSortingEvent(label))
      cnvs.EventBus.listen('Datalayer:searchResultsFilter', (label) => this.searchResultsFilterEvent(label))

      // Datalayer reference: Custom events: 7
      cnvs.EventBus.listen('Datalayer:searchBar', (label) => this.searchBarEvent(label))
      cnvs.EventBus.listen('Datalayer:socialIcon', (label) => this.socialIconEvent(label))

      // Datalayer reference: Custom events: 10
      cnvs.EventBus.listen('Datalayer:footerNewsletterSuccess', () => this.footerNewsletterSuccessEvent())

      // Datalayer reference: Custom events: 13
      cnvs.EventBus.listen('Datalayer:faqCategory', (data) => this.faqCategoryEvent(data))
      cnvs.EventBus.listen('Datalayer:faqQuestion', (data) => this.faqQuestionEvent(data))

      cnvs.EventBus.listen('Datalayer:promoImpressionsEvent', (data) => this.promoImpressionsEvent(data))
      cnvs.EventBus.listen('Datalayer:promoClickEvent', (data) => this.promoClickEvent(data))

      cnvs.EventBus.listen('Datalayer:userLogInEvent', (data) => this.userLogInEvent(data))
      cnvs.EventBus.listen('Datalayer:userRegisterEvent', (data) => this.userRegisterEvent(data))
    },

    /**
     * Format the price for the datalayer.
     * - Converts a cents number to a decimal with 2 decimal places.
     * - e.g 4000 -> '40.00'
     * @param {Number} price - price value.
     * @returns {String|Boolean} formatted price - price string or false
     */
    formatMoneyForDataLayer(price) {
      if (typeof price !== 'number') {
        return false
      }

      return (price / 100).toFixed(2)
    },

    /**
     * Get the page type.
     * - Page type formatted for datalayer.
     */
    getPageType() {
      switch (cnvs.page.type) {
        case 'cart':
          return 'cart page'
        case 'collection':
          return 'category page'
        case 'index':
          return 'home page'
        case 'product':
          return 'pdp page'
        default:
          return 'other'
      }
    },

    /**
     * Get formatted product price.
     * @param {Object} product - product data.
     */
    getProductPrice(product) {
      return product?.price?.amount ? product?.price?.amount : product.price * 100
    },

    /**
     * Get product variant.
     * @param {Object} product - product data.
     */
    getVariant(product) {
      let variantTitle = ''

      if (product.variant_title) {
        variantTitle = product.variant_title
      } else {
        variantTitle = product.variants[0].title
      }

      return variantTitle
    },


    productImpressionsEvent(data) {
      const productList = []

      data.visibleProducts.forEach((product) => {
        productList.push({
          name: product.title.toLowerCase(),
          id:   product.variants[0].sku,
          price: this.formatMoneyForDataLayer(this.getProductPrice(product)),
          brand: product.vendor.toLowerCase(),
          category: data.category.toLowerCase(),
          variant: this.getVariant(product).toLowerCase(),
          list: data.list.toLowerCase(),
          position: product.position,
        })
      })

      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'ecommerce',
        eventAction: 'product impressions',
        ecommerce: {
          currencyCode: cnvs.store.currency,
          impressions: productList,
        },
      })
    },

    /**
     * Push product click event to the datalayer.
     * Datalayer reference: Product click
     */
    productClickEvent(data) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'ecommerce',
        eventAction: 'productClick',
        ecommerce: {
          click: {
            actionField: {
              list: data.list,
            },
            products: [
              {
                name: data.product.title.toLowerCase(),
                  id: data.product.sku,
                price: this.formatMoneyForDataLayer(this.getProductPrice(data.product)),
                brand: data.product.vendor.toLowerCase(),
                category: data.category.toLowerCase(),
                variant: this.getVariant(data.product).toLowerCase(),
                position: data.index + 1,
              },
            ],
          },
        },
        eventCallback: function() {

          /**
           * Ignore redirect if `skipPreventDefault` data is sent.
           * - See product-card.vue for details
           */
          if (!data.skipPreventDefault) {
            document.location = data.product.url
          }
        },
        eventTimeout: 2000,
      })
    },

    /**
     * Push product details event to the datalayer.
     * Datalayer reference: Product details
     */
    productDetailsEvent(product) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'ecommmerce',
        eventAction: 'product detail view',
        ecommerce: {
          detail: {
            products: [
              {
                name: product.title.toLowerCase(),
                id: product.variants[0].sku,
                price: this.formatMoneyForDataLayer(product.price.amount),
                brand: product.vendor.toLowerCase(),
                category: product.type.toLowerCase(),
                dimension15: product.available ? 'InStock' : 'OutOfStock',
              },
            ],
          },
        },
      })
    },

    /**
     * Push product add to cart event to the datalayer.
     * Datalayer reference: Product add to cart
     */
    productAddToCartEvent(data) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'ecommerce',
        eventAction: 'addtoCart',
        ecommerce: {
          currencyCode: cnvs.store.currency,
          add: {
            products: [
              {
                name: data.product_title.toLowerCase(),
                id: data.sku,
                price: this.formatMoneyForDataLayer(data.price),
                brand: data.vendor.toLowerCase(),
                category: data.product_type.toLowerCase(),
                variant: data.variant_title
                  ? data.variant_title.toLowerCase() : data.variant_title,
                quantity: data.quantity,
              },
            ],
          },
        },
      })
    },

    /**
     * Push product remove from cart event to the datalayer.
     * Datalayer reference: Product remove from cart
     */
    productRemoveFromCartEvent(data) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'ecommerce',
        eventAction: 'removeFromCart',
        ecommerce: {
          remove: {
            products: [
              {
                name: data.product_title.toLowerCase(),
                id: data.sku,
                price: this.formatMoneyForDataLayer(data.price),
                brand: data.vendor.toLowerCase(),
                category: data.product_type.toLowerCase(),
                variant: data.variant_title
                  ? data.variant_title.toLowerCase() : data.variant_title,
                quantity: Math.abs(data.quantity),
              },
            ],
          },
        },
      })
    },

    /**
     * Push clicked mega nav link event to the data layer.
     * @param {String} label - clicked link title.
     * @param {Number} tier - clicked link tier.
     * Datalayer reference: 1.1
     */
    megaNavLinkEvent(data) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'navigation',
        eventAction: `level ${data.tier} click`,
        eventLabel: data.label.toLowerCase(),
        nonInteraction: 0,
      })
    },

    /**
     * Push clicked site header link event to the data layer.
     * @param {String} label - clicked link title.
     * Datalayer reference: 1.2
     */
    siteHeaderLinkEvent(label) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'navigation',
        eventAction: 'top header links',
        eventLabel: label,
        nonInteraction: 0,
      })
    },

    /**
     * Push clicked footer link event to the data layer.
     * @param {String} label - clicked link title.
     * Datalayer reference: 1.3
     */
    footerLinkEvent(label) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'navigation',
        eventAction: 'footer links',
        eventLabel: label,
        nonInteraction: 0,
      })
    },

    /**
     * Push clicked site header logo event to the data layer.
     * Datalayer reference: 1.4
     */
    siteHeaderLogoEvent() {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'navigation',
        eventAction: 'logo click',
        eventLabel: 'logo click',
        nonInteraction: 0,
      })
    },

    /**
     * Push clicked category link event to the data layer.
     * @param {String} section - clicked link's parent section.
     * @param {String} title - clicked link's title.
     * Datalayer reference: 1.5
     */
    categoryListingEvent(data) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'navigation',
        eventAction: 'featured category',
        eventLabel: `${data.section}: ${data.title}`,
        nonInteraction: 0,
      })
    },

    /**
     * Push product out of stock event to the datalayer.
     * @param {Object} product - the product object.
     * Datalayer reference: 2.1
     */
    productOutOfStockEvent(product) {
      const sku = product.variants[0].sku
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'pdp page',
        eventAction: 'product oos view',
        eventLabel: `${product.title.toLowerCase()} | ${sku} | ${this.formatMoneyForDataLayer(product.price.amount)}`,
        nonInteraction: 0,
      })
    },

    /**
     * Push clicked fullscreen product gallery event to the datalayer.
     * @param {Object} product - the product object.
     * Datalayer reference: 2.4
     */
    fullscreenProductGalleryEvent(product) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'pdp page',
        eventAction: 'pdp enlarge image',
        eventLabel: `${product.description.toLowerCase().replace( /(<([^>]+)>)/ig, '')} | ${product.variants[0].sku}`,
        nonInteraction: 0,
      })
    },

    /**
     * Push clicked product add to wishlist event to the datalayer.
     * @param {Object} productId - the id of the added product.
     * Datalayer reference: 2.5
     */
    productAddToWishlist(productId) {
      const eventCategory = this.getPageType()

      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory,
        eventAction: 'add to favorite click',
        eventLabel: productId,
        nonInteraction: 0,
      })
    },

    /**
     * Push clicked product info accordion event to the datalayer.
     * @param {Object} label - the title of the clicked option.
     * Datalayer reference: 2.7
     */
    productInfoAccordionEvent(label) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'pdp page',
        eventAction: 'horizontal product navigation',
        eventLabel: label,
        nonInteraction: 0,
      })
    },

    /**
     * Push clicked line item remove event to the datalayer.
     * @param {Object} item - the removed line item object.
     * Datalayer reference: 3.2
     */
    lineItemRemoveEvent(data) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'cart page',
        eventAction: 'removed product',
        eventLabel: `${data.product_title.toLowerCase()} | ${this.formatMoneyForDataLayer(data.price)} | ${data.quantity >= 0 ? data.quantity : 0}`,
        nonInteraction: 0,
      })
    },

    /**
     * Push search results sort by event to the datalayer.
     * @param {String} label - selected sort by label.
     * Datalayer reference: 6.1
     */
    searchResultsSortingEvent(label) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'filter',
        eventAction: 'sort click',
        eventLabel: label.toLowerCase(),
        nonInteraction: 0,
      })
    },

    /**
     * Push search results filter event to the datalayer.
     * @param {String} label - selected filter label.
     * Datalayer reference: 6.2
     */
    searchResultsFilterEvent(label) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'filter',
        eventAction: 'left filter',
        eventLabel: label,
        nonInteraction: 0,
      })
    },

    /**
     * Push clicked social icon link event to the datalayer.
     * @param {String} label - clicked icon type.
     * Datalayer reference: 7.1
     */
    searchBarEvent(label) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'search bar',
        eventAction: 'click',
        eventLabel: label,
        nonInteraction: 0,
      })
    },

    /**
     * Push clicked social icon link event to the datalayer.
     * @param {String} label - clicked icon type.
     * Datalayer reference: 7.2
     */
    socialIconEvent(label) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'social',
        eventAction: 'social icon click',
        eventLabel: label,
        nonInteraction: 0,
      })
    },

    /**
     * Push successful newsletter subscription event to the datalayer.
     * Datalayer reference: 10.1
     */
    footerNewsletterSuccessEvent() {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'newsletter subscription',
        eventAction: 'submission',
        eventLabel: 'success',
        nonInteraction: 0,
      })
    },

    /**
     * Push clicked faq category event to the datalayer.
     * @param {String} category - clicked faq category.
     * Datalayer reference: 13.1
     */
    faqCategoryEvent(data) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'FAQs',
        eventAction: 'category select',
        eventLabel: data.category,
        nonInteraction: 0,
      })
    },

    /**
     * Push clicked faq question event to the datalayer.
     * @param {String} category - clicked faq category.
     * @param {String} question - clicked faq question.
     * Datalayer reference: 13.2
     */
    faqQuestionEvent(data) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'FAQs',
        eventAction: 'question select',
        eventLabel: `${data.category}: ${data.question}`,
        nonInteraction: 0,
      })
    },

    /**
     * Push promo click event info to datalayer.
     * @param {Object} data - promo click event data.
     */
    promoClickEvent(data) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'ecommerce',
        eventAction: 'internal promotion clicks',
        eventLabel: data.pageTemplate,
        nonInteraction: 0,
        ecommerce: {
          promoClick: {
            promotions: data.promoArray,
          },
        },
      })
    },

    /**
     * Push promo impressions info to datalayer.
     * @param {Object} data - promo impressions data.
     */
    promoImpressionsEvent(data) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event: 'eventTracker',
        eventCategory: 'ecommerce',
        eventAction: 'internal promotion impressions',
        eventLabel: data.pageTemplate,
        nonInteraction: 1,
        ecommerce: {
          promoView: {
            promotions: data.promoArray,
          },
        },
      })
    },

    /**
     * User log in impressions info to datalayer.
     * @param {String} email - user email.
     */
    userLogInEvent(email) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event : 'eventTracker',
        eventCategory: 'account',
        eventAction: 'sign In successful',
        eventLabel: 'email',
        nonInteraction: 0,
        userIDCD: email,
      })
    },

    /**
     * User register impressions info to datalayer.
     * @param {Object} data - user data.
     */
    userRegisterEvent(data) {
      window.dataLayer = window.dataLayer || []

      window.dataLayer.push({
        ecommerce: null,
      })

      window.dataLayer.push({
        event : 'eventTracker',
        eventCategory: 'account',
        eventAction: 'register',
        eventLabel: `email | ${data.firstName} ${data.lastName}`,
        nonInteraction: 0,
        userIDCD: data.email,
      })
    },
  },
}
</script>